'use client';

import { useQuery } from '@tanstack/react-query';
import { IEvent } from '@/lib/types';
import { SupportedLanguages } from '@/types/globals';
import dayjs from 'dayjs';

interface CalendarResponse {
  events: IEvent[];
  count: number;
  view: string;
  dateRange: {
    start: string | null;
    end: string | null;
  };
}

interface UseEventCalendarProps {
  locale: SupportedLanguages;
  view: 'month' | 'week' | 'list';
  currentDate: dayjs.Dayjs;
  enabled?: boolean;
}

export function useEventCalendar({
  locale,
  view,
  currentDate,
  enabled = true,
}: UseEventCalendarProps) {
  // Calculate date range based on view
  const getDateRange = () => {
    let startDate: dayjs.Dayjs;
    let endDate: dayjs.Dayjs;

    switch (view) {
      case 'month':
        startDate = currentDate.startOf('month').startOf('week');
        endDate = currentDate.endOf('month').endOf('week');
        break;
      case 'week':
        startDate = currentDate.startOf('week');
        endDate = currentDate.endOf('week');
        break;
      case 'list':
        startDate = dayjs(); // Today
        endDate = dayjs().add(1, 'year'); // Next year for upcoming events
        break;
      default:
        startDate = currentDate.startOf('month');
        endDate = currentDate.endOf('month');
    }

    return {
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD'),
    };
  };

  const dateRange = getDateRange();

  return useQuery<CalendarResponse>({
    queryKey: ['calendar-events', locale, view, dateRange.start, dateRange.end],
    queryFn: async () => {
      const params = new URLSearchParams({
        locale,
        view,
        start: dateRange.start,
        end: dateRange.end,
      });

      const response = await fetch(`/api/events/calendar?${params}`);

      if (!response.ok) {
        throw new Error(`Calendar fetch failed: ${response.statusText}`);
      }

      return response.json();
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime)
    refetchOnWindowFocus: false,
    retry: 2,
  });
}

// Hook for fetching all events (fallback for when API isn't available)
export function useAllEvents(locale: SupportedLanguages, enabled = true) {
  return useQuery<IEvent[]>({
    queryKey: ['all-events', locale],
    queryFn: async () => {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_WP_JSON}/wp/v2/event?per_page=100&orderby=meta_value&meta_key=event_date_start&order=asc`,
      );

      if (!response.ok) {
        throw new Error(`Events fetch failed: ${response.statusText}`);
      }

      const events = await response.json();

      // Filter by locale and display flag
      return events.filter((event: IEvent) => {
        const eventLocale = event.acf?.locale || 'en';
        return eventLocale === locale && event.acf?.display;
      });
    },
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });
}
